import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import styles from "../Accounts/Accounts.module.scss";
import {
  CommonButton,
  CustomRequiredInputLabel,
} from "../../common/StyledComponents/StyledFields";
import CustomSelectField from "../../common/CustomSelectField/CustomSelectField";
import ErrorPopup from "../../common/ErrorPopup";
import {
  apiRequest,
  catchErrorMessageHandler,
} from "../../../infrastructure/backendService";
import apiPath from "../../../config/apiPaths.json";
import Pagination from "../../common/pagination/pagination";
import deleteIcon from "../../../assets/DeleteIcon.svg";
import ConfirmationDialog from "../../common/CofirmationDialog/ConfirmationDialog";
import SuccessPopup from "../../devotee/donation/successPopup";
import CreateSubAccount from "./CreateSubAccount";
import PageLoader from "../../common/PageLoader/PageLoader";

const SubAccounts = () => {
  const [mainAccount, setMainAccount] = useState<any>("");
  const [expenseAccounts, setExpenseAccounts] = useState<any>([]);
  const [subAccounts, setSubAccounts] = useState<any>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedSubAccount, setSelectedSubAccount] = useState<any>(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const getExpenseAccounts = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.getAllExpenseAccounts,
      includeToken: true,
      params: {
        page: 0,
        size: 300,
        direction: "asc",
        sortBy: "description",
      },
    })
      .then((res) => {
        setExpenseAccounts(res?.data?.content);
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const getAllSubAccounts = (value: any, pageNumber: number) => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.getAllSubAccounts,
      includeToken: true,
      params: {
        page: pageNumber - 1,
        size: 20,
        direction: "asc",
        sortBy: "accountId",
        accountId: value,
      },
    })
      .then((res) => {
        setSubAccounts(res?.data?.content);
        setTotalRecords(res?.data?.totalElements);
        setPageLoading(false);
        const element = document.getElementById("expense_sub_accounts");
        if (element) {
          element?.scrollIntoView({ behavior: "smooth", block: "start" });
          // Apply additional offset for a custom position
          window.scrollTo({
            top: 300,
            left: 0,
            behavior: "smooth",
          });
        }
      })
      .catch((err) => {
        setPageLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  useEffect(() => {
    getExpenseAccounts();
  }, []);

  useEffect(() => {
    if (mainAccount) {
      setPageLoading(true);
      getAllSubAccounts(mainAccount, pageNumber);
    }
  }, [pageNumber]);

  const handleFieldChange = (field: string, value: any) => {
    setMainAccount(value);
    getAllSubAccounts(value, 1);
  };

  const handleDeleteSubAccount = (subAccountDetails: any) => {
    setSelectedSubAccount(subAccountDetails);
    setOpenConfirmationDialog(true);
  };

  const handleCloseConfirmationDialog = () => {
    setOpenConfirmationDialog(false);
    setSelectedSubAccount(null);
  };

  const deleteSubAccount = () => {
    setIsBtnLoading(true);
    apiRequest({
      method: "DELETE",
      endpoint: `${apiPath.createAccount}/${selectedSubAccount?.id}`,
      includeToken: true,
    })
      .then((res) => {
        setSelectedSubAccount(null);
        setOpenConfirmationDialog(false);
        setIsBtnLoading(false);
        if (pageNumber === 1) {
          getAllSubAccounts(mainAccount, 1);
        } else {
          setPageNumber(1);
        }
        toast.custom(
          <SuccessPopup
            headerMessage="Delete Sub-Account"
            showMessage={`Successfully deleted the sub-account`}
          />
        );
      })
      .catch((err) => {
        setSelectedSubAccount(null);
        setOpenConfirmationDialog(false);
        setIsBtnLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const handleClose = () => {
    setCreateAccount(false);
  };

  const handleCloseGetAccounts = () => {
    getAllSubAccounts(mainAccount, 1);
    handleClose();
  };

  const renderHeaderSection = () => {
    return (
      <Box className="common_box_shadow" sx={{ mt: "20px" }}>
        {CustomRequiredInputLabel("Primary Account", true)}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
          }}
        >
          <Box
            sx={{
              maxWidth: "100%",
              width: "320px",
              mr: { xs: "0px", sm: "20px" },
            }}
          >
            <CustomSelectField
              handleDropdownChange={handleFieldChange}
              errorMessage={""}
              dropDownList={expenseAccounts}
              value={mainAccount}
              fieldName="mainAccount"
              placeholder="Select main account"
              optionLabel="description"
              optionValue="accountId"
              isLabelValue={true}
            />
          </Box>
          <CommonButton
            onClick={() => {
              setCreateAccount(true);
            }}
            disabled={!mainAccount}
          >
            Create New Sub-Account
          </CommonButton>
        </Box>
      </Box>
    );
  };

  const renderViewSubAccounts = () => {
    return (
      <Box sx={{ mt: "20px" }}>
        <Box
          sx={{ width: "100%", overflowX: "auto" }}
          id="expense_sub_accounts"
        >
          <table className={styles.table_container}>
            <thead>
              <th>Sr No</th>
              <th>Sub-Account ID</th>
              <th>Sub Account Title</th>
              <th>Category</th>
              <th>Action</th>
            </thead>
            <tbody>
              {subAccounts?.map?.((row: any, index: number) => (
                <tr key={index}>
                  <td>{index + 1 + (pageNumber - 1) * 20}</td>
                  <td className={`${row?.deleted ? styles.deleted_acc : ""}`}>
                    {row?.accountId}
                  </td>
                  <td className={`${row?.deleted ? styles.deleted_acc : ""}`}>
                    {row?.description}
                  </td>
                  <td className={`${row?.deleted ? styles.deleted_acc : ""}`}>
                    {row?.categoryName}
                  </td>
                  <td className={`${row?.deleted ? styles.deleted_acc : ""}`}>
                    {!row?.deleted ? (
                      <Box sx={{ width: "35px", textAlign: "center" }}>
                        <Box
                          component={"img"}
                          src={deleteIcon}
                          alt="Delete"
                          onClick={() => {
                            handleDeleteSubAccount(row);
                          }}
                          sx={{ cursor: "pointer" }}
                        />
                      </Box>
                    ) : (
                      "Deleted"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        {totalRecords > 0 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "15px" }}>
            <Pagination
              items={totalRecords}
              itemsPerPage={20}
              setPageNumber={setPageNumber}
              pageNumber={pageNumber}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderEmptyData = () => {
    return (
      <Box className="common_box_shadow" sx={{ mt: "20px" }}>
        <Typography className="sub_heading">No data found</Typography>
      </Box>
    );
  };

  return (
    <Box className={styles.view_requests_main}>
      <Toaster />
      <Typography className="sub_heading">Sub-Accounts</Typography>
      {renderHeaderSection()}
      {mainAccount &&
        (subAccounts?.length > 0 ? renderViewSubAccounts() : renderEmptyData())}
      {openConfirmationDialog && (
        <ConfirmationDialog
          title={"Please confirm"}
          description={`Do you want to delete this sub-account? <b>${selectedSubAccount?.description} (${selectedSubAccount?.accountId})</b>`}
          handleProceed={deleteSubAccount}
          handleClose={handleCloseConfirmationDialog}
          open={openConfirmationDialog}
          isBtnLoading={isBtnLoading}
        />
      )}
      {createAccount && (
        <CreateSubAccount
          open={createAccount}
          handleClose={handleClose}
          handleCloseGetAccounts={handleCloseGetAccounts}
          mainAccount={mainAccount}
          expenseAccounts={expenseAccounts}
        />
      )}
      <PageLoader loading={pageLoading} />
    </Box>
  );
};

export default SubAccounts;
